import React, { useState, useCallback, useRef } from 'react';
import type { Game, Round, PlayerStats } from '@aco/shared';
import { useAuth } from '../context/AuthContext.js';
import { ScoreCorrectionFlow } from './ScoreCorrectionFlow.js';
import { RoundSelectionDialog } from './RoundSelectionDialog.js';

// ── Types ──────────────────────────────────────────────────

type GearMenuView =
  | { type: 'menu' }
  | { type: 'round-list' }
  | { type: 'correct-round'; round: Round };

interface GearMenuProps {
  game: Game;
  onClose: () => void;
  /** Closes all overlays including the gear menu — used by the correction flow. */
  onCloseAll: () => void;
  onToggleScoringMode: () => Promise<void>;
  onCorrectRound: (round: Round, newTeam1Score: number, newTeam2Score: number, playerStats?: PlayerStats[]) => Promise<void>;
}

// ── Shared styles ──────────────────────────────────────────

/**
 * Full-screen backdrop — click-to-dismiss surface.
 * Uses flex column so gearAnchorStyle at the bottom-left acts as the anchor.
 */
const menuOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 200,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  padding: '0 0 24px 24px',
};

/**
 * Relative-positioned wrapper anchoring the popup card above itself.
 * position:relative is mandatory — the card's bottom:100% resolves against this div.
 */
const gearAnchorStyle: React.CSSProperties = {
  position: 'relative',
};

/** Popup card — floats above the gear anchor via absolute bottom:100% */
const menuCardStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: '0',
  marginBottom: '8px',
  zIndex: 1000,
  background: '#1a1a2e',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.12)',
  minWidth: '240px',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  fontFamily: "'Inter', sans-serif",
};

/**
 * Menu item font size and weight match the Cancel button in the ScoreEntry
 * action row (fontSize ~1rem, fontWeight normal/500).
 */
const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '14px 20px',
  minHeight: '52px',
  fontSize: '1rem',
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  color: '#fff',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  width: '100%',
  textAlign: 'left',
  borderBottom: '1px solid rgba(255,255,255,0.07)',
  touchAction: 'manipulation',
};

const menuTitleStyle: React.CSSProperties = {
  padding: '12px 20px 10px',
  fontSize: '0.75rem',
  fontWeight: 700,
  fontFamily: "'Inter', sans-serif",
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  opacity: 0.45,
  color: '#fff',
};

// ── Component ──────────────────────────────────────────────

/**
 * Floating gear menu with:
 *   - "Switch scoring mode" toggle
 *   - "Correct a Round" → round list → ScoreCorrectionFlow
 *
 * The popup card appears ABOVE the gear ⚙️ button (which is at the bottom of
 * the screen). Clicking the backdrop dismisses the menu.
 */
export function GearMenu({ game, onClose, onCloseAll, onToggleScoringMode, onCorrectRound }: GearMenuProps) {
  const [view, setView] = useState<GearMenuView>({ type: 'menu' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { updatePreferences } = useAuth();

  const isTournament = Boolean(game.tournamentId);
  const canToggleScoringMode =
    game.scoringMode !== 'simple' &&
    (!isTournament || game.tournamentConfig?.allowScoringModeToggle === true);

  const completedRounds = game.rounds.slice().sort((a, b) => a.roundNumber - b.roundNumber);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose],
  );

  const handleToggleScoringMode = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onToggleScoringMode();
      // Persist preference — derive new mode (toggle from current)
      const newMode = game.scoringMode === 'intermediate' ? 'advanced' : 'intermediate';
      updatePreferences({ scoringMode: newMode }); // fire-and-forget
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [onToggleScoringMode, onClose, game.scoringMode, updatePreferences]);

  const handleCorrectRoundConfirm = useCallback(
    async (round: Round, newTeam1Score: number, newTeam2Score: number, playerStats?: PlayerStats[]) => {
      await onCorrectRound(round, newTeam1Score, newTeam2Score, playerStats);
      // Close everything — return directly to scoreboard after a successful correction
      onCloseAll();
    },
    [onCorrectRound, onCloseAll],
  );

  // ── View: score correction entry + confirm dialog ─────────
  if (view.type === 'correct-round') {
    return (
      <ScoreCorrectionFlow
        game={game}
        round={view.round}
        onConfirm={handleCorrectRoundConfirm}
        onDismiss={onCloseAll}
      />
    );
  }

  // ── View: round selection dialog ─────────────────────────
  if (view.type === 'round-list') {
    return (
      <RoundSelectionDialog
        completedRounds={completedRounds}
        game={game}
        onSelect={(round) => setView({ type: 'correct-round', round })}
        onCancel={onCloseAll}
      />
    );
  }

  // ── View: main gear menu ──────────────────────────────────
  return (
    <div ref={overlayRef} style={menuOverlayStyle} onClick={handleOverlayClick}>
      <div style={gearAnchorStyle}>
        <div style={menuCardStyle}>
          <div style={menuTitleStyle}>Game Settings</div>

          {canToggleScoringMode && (
            <button
              style={menuItemStyle}
              onClick={handleToggleScoringMode}
              disabled={isSubmitting}
            >
              <span style={{ fontSize: '1.2rem' }}>⚡</span>
              <span>
                Switch to {game.scoringMode === 'intermediate' ? 'Advanced' : 'Intermediate'} PPR
              </span>
            </button>
          )}

          {completedRounds.length > 0 && (
            <button
              style={{ ...menuItemStyle, borderBottom: 'none' }}
              onClick={() => setView({ type: 'round-list' })}
            >
              <span style={{ fontSize: '1.2rem' }}>✏️</span>
              <span>Correct a Round</span>
            </button>
          )}

          {!canToggleScoringMode && completedRounds.length === 0 && (
            <div style={{ padding: '14px 20px', fontSize: '0.9rem', opacity: 0.45, color: '#fff' }}>
              No actions available yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
